import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from 'store';
import { ConlicitasegState } from 'store/modules/conlicitaseg_store/types';
import { getQuotes } from 'store/modules/conlicitaseg_store/actions';
import { downloadFile, sendInsuranceQuoteToEmail } from 'services/actions/conlicitaseg_actions';
import {
  formatCurrency,
  formatCNPJ,
  formatDate,
  formatInsuranceName,
  formatStatus,
} from 'services/conlicitaseg';
import {
  ProcessInfoContainer,
  InfoCard,
  BadgeContainer,
  ActionButton,
  ContactLink,
  LoadingContainer,
} from './styles';
import ModalConlicitasegEmail from '../modal_conlicitaseg_email';
import LoadingBidding from '../loading_bidding';

const ProcessInfo: React.FC = () => {
  const dispatch = useDispatch();
  const { processId } = useParams<{ processId: string }>();
  const [processIndex, setProcessIndex] = useState<number>(-1);

  const { processes, request } = useSelector<ApplicationState, ConlicitasegState>(
    (state) => state.session.conlicitaseg,
  );

  const [showEmailModal, setShowEmailModal] = useState<boolean>(false);
  const [newEmail, setNewEmail] = useState<string>('');
  const [emails, setEmails] = useState<string[]>([]);
  const [isDownloadingPolicyOrMinuta, setIsDownloadingPolicyOrMinuta] = useState<boolean>(false);
  const [isDownloadingBoleto, setIsDownloadingBoleto] = useState<boolean>(false);

  useEffect(() => {
    setProcessIndex(processes.findIndex((process) => process.id === Number(processId)));
  }, [processes, processId]);

  useEffect(() => {
    dispatch(getQuotes());
  }, [dispatch]);

  const insurance = processes[processIndex];

  const handleBack = () => {
    window.location.href = '/conlicitaseg';
  };

  const isMinutaStatus = () =>
    insurance.selected_quote.status_id === 'analise' ||
    insurance.selected_quote.status_id === 'cotacao';

  const disableDownloadDraftOrDocument = () => {
    if (isMinutaStatus()) {
      if (insurance.selected_quote.draft_url === '' || insurance.selected_quote.draft_url === null)
        return true;
      return false;
    }
    if (insurance.selected_quote.status_id === 'emitida') {
      if (
        insurance.selected_quote.document_url === '' ||
        insurance.selected_quote.document_url === null
      )
        return true;
      return false;
    }
    return false;
  };

  const handleDownloadPolicyOrMinuta = async () => {
    if (insurance && insurance.selected_quote.insurance_company_id) {
      try {
        setIsDownloadingPolicyOrMinuta(true);
        await downloadFile(insurance.selected_quote.id, isMinutaStatus() ? 'minuta' : 'apolice');
      } finally {
        setIsDownloadingPolicyOrMinuta(false);
      }
    }
  };

  const handleAddEmail = () => {
    if (newEmail && !emails.includes(newEmail)) {
      setEmails([...emails, newEmail]);
      setNewEmail('');
    }
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter((email) => email !== emailToRemove));
  };

  const handleSendEmails = () => {
    if (insurance && insurance.selected_quote.selected_api_quote_id) {
      emails.forEach((email) => {
        sendInsuranceQuoteToEmail(insurance.selected_quote.selected_api_quote_id, email);
      });
    }
    setShowEmailModal(false);
  };

  const handleDownloadBoleto = async () => {
    if (insurance && insurance.selected_quote.insurance_company_id) {
      try {
        setIsDownloadingBoleto(true);
        await downloadFile(insurance.selected_quote.id, 'boleto');
      } finally {
        setIsDownloadingBoleto(false);
      }
    }
  };

  if (request?.get_quotes?.loading) {
    return (
      <div className="page-container">
        <div className="container py-5">
          <div className="bg-white rounded shadow-sm p-4">
            <LoadingContainer>
              <LoadingBidding
                title="Por favor, aguarde…"
                subtitle="Estamos carregando as informações do processo!"
              />
            </LoadingContainer>
          </div>
        </div>
      </div>
    );
  }

  if (!insurance) {
    return (
      <div className="page-container">
        <div className="container py-4">
          <div className="alert alert-warning">Cotação não encontrada.</div>
        </div>
      </div>
    );
  }

  return (
    <ProcessInfoContainer>
      <div className="page-container">
        <div className="container-fluid big-resolutions py-4">
          <div className="mb-4 mt-5">
            <button type="button" className="btn btn-link px-0 mb-3" onClick={handleBack}>
              <i className="material-icons align-middle">keyboard_arrow_left</i> Voltar
            </button>
          </div>

          <h3 className="mb-4">Informações do processo</h3>

          <InfoCard className="card p-4">
            <div className="row">
              <div className="col-12 col-md-9">
                <div className="row">
                  <div className="col-12 col-md">
                    <div className="mb-3">
                      <strong>Status</strong>
                      <BadgeContainer className="mb-4">
                        <span className={`badge ${formatStatus(insurance?.status).className} mr-2`}>
                          {formatStatus(insurance?.status).text}
                        </span>
                      </BadgeContainer>
                    </div>

                    <div className="mb-3">
                      <strong>Segurado</strong>
                      <span className="d-block">
                        {`${formatCNPJ(insurance.insured.cnpj)} - ${insurance.insured.name}`}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Tomador</strong>
                      <span className="d-block">
                        {`${formatCNPJ(insurance.policyholder_cnpj)} - ${
                          insurance.policyholder_company_name
                        }`}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Modalidade de licitação</strong>
                      <span className="d-block">{insurance.type_warranty_id || 'N/A'}</span>
                    </div>

                    <div className="mb-3">
                      <strong>Nº do edital </strong>
                      <span className="d-block">{insurance.bidding_edict || 'N/A'}</span>
                    </div>

                    <div className="mb-3">
                      <strong>Nº da apólice</strong>
                      <span className="d-block">
                        {insurance.selected_quote.policy_number || 'Não informado'}
                      </span>
                    </div>
                  </div>

                  <div className="col-12 col-md">
                    <div className="mb-3">
                      <strong>Vigência do seguro</strong>
                      <span className="d-block">
                        {`${formatDate(insurance?.contract_start_date)} até ${formatDate(
                          insurance?.contract_end_date,
                        )} (${insurance.contract_days} dias)` || 'N/A'}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Contratada em</strong>
                      <span className="d-block">
                        {insurance?.created_at
                          ? new Date(insurance?.created_at).toLocaleDateString('pt-BR')
                          : 'N/A'}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Total da cobertura</strong>
                      <span className="d-block">
                        {formatCurrency(insurance.contract_insured_amount)}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Seguradora escolhida</strong>
                      <span className="d-block">
                        {formatInsuranceName(insurance.selected_quote.insurance_company_id)}
                      </span>
                    </div>

                    <div className="mb-3">
                      <strong>Valor do seguro</strong>
                      <span className="d-block">
                        {formatCurrency(insurance.selected_quote.premium_value)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-3">
                <div className="d-flex flex-column">
                  <ActionButton
                    className="btn btn-primary btn-block py-2"
                    onClick={handleDownloadPolicyOrMinuta}
                    disabled={disableDownloadDraftOrDocument() || isDownloadingPolicyOrMinuta}
                  >
                    {isDownloadingPolicyOrMinuta
                      ? isMinutaStatus()
                        ? 'Baixando a minuta...'
                        : 'Baixando a apólice...'
                      : isMinutaStatus()
                      ? 'Baixar minuta'
                      : 'Baixar apólice'}
                  </ActionButton>

                  {insurance.internal_process && insurance.selected_quote.status_id === 'emitida' && (
                    <ActionButton
                      className="btn btn-primary btn-block py-2"
                      onClick={() => setShowEmailModal(true)}
                      disabled={
                        !insurance.selected_quote.document_url || !insurance.selected_quote.bill_url
                      }
                    >
                      Enviar por e-mail
                    </ActionButton>
                  )}

                  {insurance.internal_process && insurance.selected_quote.status_id === 'emitida' && (
                    <ActionButton
                      className="btn btn-primary btn-block py-2"
                      onClick={handleDownloadBoleto}
                      disabled={
                        insurance.selected_quote.bill_url === '' ||
                        insurance.selected_quote.bill_url === null ||
                        isDownloadingBoleto
                      }
                    >
                      {isDownloadingBoleto ? 'Baixando o boleto...' : 'Baixar boleto'}
                    </ActionButton>
                  )}
                </div>

                <div className="mt-4 text-center">
                  <p className="text-muted mb-2">
                    Caso necessite de cancelamento,{' '}
                    <ContactLink
                      href="/conlicitaseg/contato"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      entre em contato com nossa equipe.
                    </ContactLink>
                  </p>
                </div>
              </div>
            </div>
          </InfoCard>
        </div>
      </div>

      <ModalConlicitasegEmail
        showEmailModal={showEmailModal}
        setShowEmailModal={setShowEmailModal}
        newEmail={newEmail}
        setNewEmail={setNewEmail}
        emails={emails}
        handleAddEmail={handleAddEmail}
        handleRemoveEmail={handleRemoveEmail}
        handleSendEmails={handleSendEmails}
      />
    </ProcessInfoContainer>
  );
};

export default ProcessInfo;
